import React, { createContext, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import adminApis from "src/api/adminNotification";
import tenantApis from "src/api/notification";
import { useAppDispatch } from "src/redux/hooks";
import * as adminActions from "src/redux/slices/adminNotificationSlice";
import * as tenantActions from "src/redux/slices/notificationSlice";
import { AppNotification } from "src/types/notification.types";

const NotificationsContext = createContext<AppNotification[]>([]);
export const useLiveNotifications = () => useContext(NotificationsContext);

const getPermission = () => typeof Notification !== "undefined" && window.isSecureContext
  ? Notification.permission : null;

// Every page wraps its own <Layout>, so LiveNotifications fully remounts on
// each navigation — a local (per-effect) "already seen" set would reset on
// every route change and could silently swallow a notification that arrived
// in the gap. Module-level state survives remounts within the same page
// load; only a hard reload starts it over (harmless — the first poll after
// a reload never alerts, same as today).
const seenByType: Record<"admin" | "tenant", Set<string> | null> = {
  admin: null,
  tenant: null,
};

export default function LiveNotifications({ type, children }: {
  type: "admin" | "tenant"; children: React.ReactNode;
}) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [notes, setNotes] = useState<AppNotification[]>([]);
  const [permission, setPermission] = useState(getPermission);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let stopped = false;
    let busy = false;
    const api = type === "admin" ? adminApis : tenantApis;
    const actions = type === "admin" ? adminActions : tenantActions;
    const refresh = async () => {
      if (busy || stopped) return;
      busy = true;
      try {
        const [list, count] = await Promise.all([api.getNotificationList({ page: 1, limit: 50 }), api.getUnreadCount()]);
        if (stopped) return;
        const latest: AppNotification[] = list.data || [];
        setNotes(latest);
        dispatch(actions.getUnreadCountSuccess(count.data?.count || 0));
        const seen = seenByType[type];
        const currentPermission = getPermission();
        for (const note of latest) {
          if (seen && !seen.has(note._id) && !note.isRead) {
            if (currentPermission !== "granted") continue;
            try {
              const alert = new Notification(note.title, { body: note.message, tag: note._id });
              alert.onclick = () => {
                window.focus();
                if (note.link?.startsWith("/") && !note.link.startsWith("//")) navigate(note.link);
                alert.close();
              };
            } catch (err) {
              // Most likely an OS-level block (e.g. macOS System Settings ->
              // Notifications -> the browser itself) rather than a code bug —
              // the in-page permission can read "granted" while the OS still
              // suppresses the banner. Logged so that's visible instead of a
              // silent no-op; the in-app bell keeps working regardless.
              console.warn("Browser notification failed to display:", err);
            }
          }
        }
        seenByType[type] = new Set(latest.map((note) => note._id));
      } catch {
        // Preserve the last successful result and retry on the next tick.
      } finally {
        busy = false;
      }
    };
    const onFocus = () => { setPermission(getPermission()); void refresh(); };
    void refresh();
    const timer = window.setInterval(refresh, 3000);
    window.addEventListener("focus", onFocus);
    return () => { stopped = true; window.clearInterval(timer); window.removeEventListener("focus", onFocus); };
  }, [type, dispatch, navigate]);

  const enable = async () => {
    try { setPermission(await Notification.requestPermission()); }
    catch { setDismissed(true); }
  };

  return <NotificationsContext.Provider value={notes}>
    {!dismissed && permission !== null && permission !== "granted" && (
      <div className="flex shrink-0 flex-wrap items-center justify-center gap-3 border-b border-primary-100 bg-primary-50 px-4 py-2 text-xs text-primary-800 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200">
        <span>{permission === "denied" ? "Browser alerts are blocked. Allow notifications in your browser’s site settings." : "Enable browser notifications to get alerts for new messages."}</span>
        {permission === "default" && <button onClick={enable} className="font-semibold underline">Enable notifications</button>}
        <button onClick={() => setDismissed(true)} aria-label="Dismiss notification prompt" className="text-gray-500">Dismiss</button>
      </div>
    )}
    {children}
  </NotificationsContext.Provider>;
}
